import { z } from 'zod';

const REQUEST_PREFIX = 'tinqe-flows';

export abstract class AbstractStateService<T> {
  protected abstract storageKey: string;
  protected abstract schema: z.ZodType<T, z.ZodTypeDef, unknown>;

  /**
   * Where the state is kept. Defaults to `localStorage`, which outlives the tab.
   * A flow whose data belongs to a session rather than to the device — the
   * upsell, which runs on a token that expires — overrides this with
   * `sessionStorage`.
   */
  protected storage: Storage = localStorage;

  /**
   * Namespace of the storage keys. A second flow overrides it so its state sits
   * beside the conversion flow's instead of on top of it.
   */
  protected keyPrefix: string = REQUEST_PREFIX;

  get key(): string {
    return `${this.keyPrefix}_${this.storageKey}`;
  }

  public getState<T>(): T | null {
    const state = this.storage.getItem(this.key);
    if (state) {
      const parsedState = JSON.parse(state);
      const parseResult = this.schema.safeParse(parsedState);
      if (!parseResult.success) {
        console.error(
          'Invalid state: ' + JSON.stringify(parseResult.error.errors),
        );
        return null;
      }
      return parsedState;
    }
    return null;
  }

  public saveState<T>(state: T): T {
    const parseResult = this.schema.safeParse(state);
    if (!parseResult.success) {
      throw new Error(
        'Invalid state: ' + JSON.stringify(parseResult.error.errors),
      );
    }
    const newState = JSON.stringify(state);

    this.storage.setItem(this.key, newState);
    return state;
  }

  public clearState(): void {
    this.storage.removeItem(this.key);
  }
}
